import styles from "@/styles/login.module.scss"
import { Button, Form, Input, message, Divider } from 'antd'
import Image from 'next/image'
import Link from 'next/link'
import { useEffect, useState, useRef } from "react"
import { setCookie } from '@/utils/cookie'
import loginApi from '@/service/modules/loginApi'
import { updateUserInfo } from '@/store/slices/userInfoSlice';
import { useDispatch } from "react-redux"
import { useGoogleLoginUtils } from '@/utils/googleLogin'; // 替换为实际的路径

// @ts-ignore
import ReCAPTCHA from 'react-google-recaptcha';
import { matomoSetCustomDimension } from "@/utils/matomoEvent"

interface Props {
  onSwitchType: (type: string) => void; // 通知父组件 请求接口修改数据
  onSuccessJump: () => void; // 通知父组件 请求接口修改数据
}

export default function LoginForm({ onSwitchType, onSuccessJump }: Props) {
  const recaptchaRef = useRef<ReCAPTCHA | null>(null);

  const dispatch = useDispatch();
  const [form] = Form.useForm();

  const [recaptchaSiteKey, setRecaptchaSiteKey] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [loading, setLoading] = useState(false);


  // 获取信用卡支付错误次数以及Recaptcha key
  const getRecaptchaSiteKey = async() => {
    try {
      let res = await loginApi.LoginErrorCheck()
      if (res && res.data) {
        setRecaptchaSiteKey(res.data.siteKey || '')
      }
    } catch (error: any) {
      message.error(error.message);
      console.log(error.response);
    }
  }

  // 获取Recaptcha token
  const handleRecaptchaChange = (value: string | null) => {
    setRecaptchaToken(value || '')
  };

  useEffect(() => {
    getRecaptchaSiteKey()
  }, [])

  // 校验Login表单成功，发起提交请求
  const onFinishLoginForm = async (values: any) => {
    try {
      const gRecaptchaResponse = recaptchaRef.current?.getValue();
      if (recaptchaSiteKey && !gRecaptchaResponse) {
        message.error('Please verify you are not a robot')
        return
      }

      setLoading(true)

      let dataObj = {
        email: values.email.trim(),
        password: window.btoa(values.password),
        recaptcha: ''
      }

      if (recaptchaSiteKey) {
        dataObj.recaptcha = gRecaptchaResponse
      }

      const finnalParams = Object.fromEntries(
        Object.entries(dataObj).filter(([key, value]) => value !== null && value !== '')
      );

      let res = await loginApi.login(finnalParams)
      setLoading(false)
      if (res.data) {
        console.log(res, ' == res');
        message.success('Login Successful!');

        // matomo记录登录结果
        let loginResult = 0
        if (window && (window as any)._paq) {
          (window as any)._paq.push(['setUserId', res.data.customerID]);
        }
        matomoSetCustomDimension(23, {loginMethod: 'login', loginResult})

        // userEmail存起来，方便数据埋点使用
        if (res.data.email) {
          localStorage.setItem('userEmail', res.data.email || '')
          setCookie('userCustomerId', res.data.customerID || '')
        } else {
          localStorage.removeItem('userEmail')
        }

        // 登录成功，跳转
        if (onSuccessJump) {
          dispatch(updateUserInfo(res.data))
          setTimeout(() => {
            onSuccessJump()
          }, 1500);
        }
      }
    } catch (error: any) {
      setLoading(false)
      /* 处理错误信息 
        - 假设API错误的返回格式为：{ message: '错误信息', field: '表单字段名' }
        - 你需要根据你实际的错误响应结构来调整
      */

      if (error.message) {
        if (error.code == 115001 || error.code == 115002 || error.code == 115004) {
          // 账号或密码输入错误
          const errorMessage = error.message
          const errorField = 'password'

          form.setFields([
            {
              name: errorField,
              errors: [errorMessage],
            }
          ]);
        } else if (error.code == 110000) {
          // captchaCode输入错误
          const errorMessage = error.message
          const errorField = 'captchaCode'

          form.setFields([
            {
              name: errorField,
              errors: [errorMessage],
            }
          ]);
        } else if (error.code == 115003) {
          let currentEmail = localStorage.getItem('userEmail')
          alert(`You are already logged in to your account (${currentEmail}) in another tab.\nPlease log in to the same account in the current tab, or log out of your account (${currentEmail}) and try again.`)

          // message.error(error.message)
          // 账号已经登录，跳转回去
          if (onSuccessJump) {
            setTimeout(() => {
              onSuccessJump()
            }, 500);
          }
        } else {
          // 其他可能的错误，都放在password表单上
          const errorMessage = error.message
          const errorField = 'password'

          form.setFields([
            {
              name: errorField,
              errors: [errorMessage],
            }
          ]);
        }

        // 刷新验证码
        if (
          error.code == 115004 || error.code == 110000) {
          // generateCaptchaImage()

          if (recaptchaSiteKey) {
            recaptchaRef.current.reset();
          } else {
            getRecaptchaSiteKey()
          }
        }

      } else {
        // 如果不是一个有效的数据响应，你可以设置一个通用的错误信息
        message.error('An unexpected error occurred. Please try again.');
      }

      // matomo 记录登录失败信息
      let loginResult = error.code
      matomoSetCustomDimension(23, {loginMethod: 'login', loginResult})
    }
  }

  // 切换登录/注册
  const toRegister = async (type: string) => {
    if (onSwitchType) {
      setLoading(false)
      onSwitchType(type)
    }
  }

  // Google登录
  const onGoogleLogin = useGoogleLoginUtils({ onSuccessJump });

  return (
      <div className={styles['login-form-box']}>
        <div className={styles['title']}>Sign in</div>
        <Form
          form={form}
          name={'login'}
          layout="vertical"
          size={'large'}
          style={{ width: '100%' }}
          onFinish={onFinishLoginForm}
          autoComplete="off"
          scrollToFirstError
        >
          <Form.Item
            name="email"
            rules={[
              { required: true, message: `Email Address is required` },
              { type: 'email', message: `Invalid Email Address` },
            ]}
            getValueFromEvent={(e) => e.target.value.replace(/(^\s*)|(\s*$)/g, '')}
          >
            <Input placeholder="Email Address" maxLength={225} />
          </Form.Item>

          <Form.Item
            name="password"
            rules={[
              { required: true, message: `Password is required` },
              { min: 6, message: `Error! Password must be at least 6 characters.` },
            ]}
          >
            <Input.Password placeholder="Password" maxLength={64} />
          </Form.Item>
          
          {
            !!(recaptchaSiteKey) &&
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey={recaptchaSiteKey}
              type={'audio'}
              onChange={handleRecaptchaChange}
            />
          }
          
          <Link
            href={`/reset-password`}
            legacyBehavior
          >
            <a rel="noopener noreferrer">
              <div className={styles['forgot-password']}>Forgot password?</div>
            </a>
          </Link>

          <Form.Item style={{ marginBottom: 0 }}>
            <Button
              className={styles['save-btn']}
              htmlType="submit"
              loading={loading}
            >
              Sign in
            </Button>
          </Form.Item>
        </Form>

        <div className={styles['go-register']}>
          New to BestDeals?&nbsp;
          <span
            className={styles['link']}
            onClick={() => {
              toRegister('register')
            }}
          >
            Sign up
          </span>
        </div>

        <Divider style={{ fontSize: '14px', border: '#DDDDDD' }}>OR</Divider>

        <div className={styles['google-login']} onClick={() => onGoogleLogin()}>
          <Image
            alt={'google logo'}
            width={32}
            height={32}
            className={styles['google-icon']}
            src={'https://img.bestdeals.co.nz/images/Sign-Google.png'} />
          <div className={styles['google-login-text']}>Sign in with Google</div>
        </div>
      </div>
  )
}